import { cx } from '@emotion/css';
import {
  forwardRef,
  useCallback,
  KeyboardEvent,
  ChangeEvent,
  useEffect,
  useRef,
} from 'react';
import { useController, useFormContext } from 'react-hook-form';

import callAllFn from '@julofinance/web-helpers/dist/fn/callAllFn';
import mergeRefs from '@julofinance/web-helpers/dist/fn/mergeRefs';

import { CheckCircle } from '@new-components/shapes';

import Button from '../../../button';
import FieldGroup from '../../../field-group';
import Input from '../../../input/InputBase';

import { PromoCodeInputProps } from './types';
import { promoCodeInputCx } from './styles';
import { Typography } from '@julo-ui/react';
import AppliedPromo from '../applied-promo';

const PromoCodeInput = forwardRef<HTMLDivElement, PromoCodeInputProps>(
  (props, ref) => {
    const {
      name,
      label,
      helperText,
      className,
      inputProps,
      isVerified = false,
      isInvalid = false,
      isValidated = false,
      isVerifying,
      onVerify,
      disableVerify,
      onKeyDown,
      disabled,
      value,
      defaultValue,
      rules,
      required,
      inputRef = null,
      onChange,
      onBlur,
      ...resProps
    } = props;

    const { control, trigger } = useFormContext();
    const {
      fieldState: { error, invalid },
      field: {
        ref: controlRef,
        onChange: controllerOnChange,
        onBlur: controllerOnBlur,
        value: controllerValue,
      },
    } = useController({
      name,
      control,
      defaultValue,
      rules: {
        ...(required && { required: 'This field is required' }),
        ...(rules && rules),
      },
    });

    const prevIsValidated = useRef(isValidated);

    const handleOnKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
      if (
        e.key.length > 1 ||
        ((e.altKey || e.shiftKey || e.ctrlKey || e.metaKey) &&
          e.key.length <= 1)
      )
        return;
    }, []);

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        e.target.setAttribute('data-promo-code-invalid', 'false');
        e.target.setAttribute('data-promo-code-verified', 'false');
        e.target.setAttribute('data-promo-code-validated', 'false');
        e.target.setAttribute('data-promo-code-error-message', '');
        e.target.value = e.target.value.replace(/[^0-9A-Za-z]/g, '');
      },
      [],
    );

    useEffect(() => {
      if (prevIsValidated.current !== isValidated) {
        trigger(name);
        prevIsValidated.current = isValidated;
      }
    }, [isValidated, name, trigger]);

    return (
      <FieldGroup
        invalid={invalid}
        error={error}
        helperText={helperText}
        className={cx(promoCodeInputCx, className)}
        ref={ref}
        name={name}
      >
        {isVerified ? (
          <AppliedPromo />
        ) : (
          <>
            {!!label && <Typography type='caption'>{label}</Typography>}
            <div
              className={cx(`promo-code-wrapper`, {
                hidden: isVerified,
              })}
            >
              <Input
                {...resProps}
                ref={ref}
                className='promo-code-input'
                inputProps={{
                  ...inputProps,
                  'data-promo-code-invalid': isInvalid,
                  'data-promo-code-verified': isVerified,
                  'data-promo-code-validated': isValidated,
                  'data-promo-code-error-message': error?.message,
                }}
                inputRef={mergeRefs(controlRef, inputRef)}
                isInvalid={invalid}
                value={value ?? controllerValue}
                onChange={callAllFn(handleChange, onChange, controllerOnChange)}
                onBlur={callAllFn(onBlur, controllerOnBlur)}
                onKeyDown={callAllFn(handleOnKeyDown, onKeyDown)}
                disabled={disabled || isVerified}
                name={name}
              />
              <Button
                className='promo-code-verify-btn'
                disabled={
                  disableVerify || disabled || isVerified || isVerifying
                }
                variant={isVerified ? 'primary' : 'secondary'}
                onClick={onVerify}
              >
                {isVerified ? (
                  <>
                    <span>Terverifikasi</span>
                    <CheckCircle
                      width={20}
                      height={20}
                      className='check-icon'
                    />
                  </>
                ) : (
                  'Gunakan'
                )}
              </Button>
            </div>
          </>
        )}
      </FieldGroup>
    );
  },
);

export default PromoCodeInput;
